import { useEffect, useRef, useState, useCallback } from 'react';
import { Box, TextField, MenuItem, Button, Typography, CircularProgress } from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import icon2x from 'leaflet/dist/images/marker-icon-2x.png';
import shadow from 'leaflet/dist/images/marker-shadow.png';
import { geocodeAddress, getNearbyInstalls, runInstallsBackfill, getInstallsBackfillStatus } from '../../api/installsMapApi';
import { useUser } from '../../context/userContext';

// CRA/webpack mangles Leaflet's default marker image paths — point them at the
// bundled assets explicitly so pins actually render.
L.Icon.Default.mergeOptions({ iconUrl: icon, iconRetinaUrl: icon2x, shadowUrl: shadow });

const PP = "'Poppins', sans-serif";
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-MY', { month: 'short', year: 'numeric' }); } catch { return ''; } };
const esc = (s) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/**
 * "Neighbours Who Installed" map for RSC. Geocodes a typed area/postcode/address,
 * centers a free OpenStreetMap (Leaflet) map there, and drops a pin per installed
 * (Active) customer within the radius. Pin popup shows customer, area, system size,
 * install date and the closing agent.
 */
export default function InstallsMap({ defaultQuery = '' }) {
    const mapElRef = useRef(null);
    const mapRef = useRef(null);
    const layerRef = useRef(null);
    const [query, setQuery] = useState(defaultQuery);
    const [radius, setRadius] = useState(10);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [count, setCount] = useState(null);

    // Super Admin can refresh the map: geocode newly-added Active installs that have no
    // coordinates yet, so they appear on the map. (user 2026-08-06)
    const { user } = useUser();
    const isSuperAdmin = Array.isArray(user?.roles) && user.roles.includes('Super Admin');
    const [backfilling, setBackfilling] = useState(false);
    const [backfillMsg, setBackfillMsg] = useState('');
    const updateMap = useCallback(async () => {
        setBackfilling(true); setBackfillMsg('Starting…');
        try {
            await runInstallsBackfill();
            // Poll status until the background run finishes (cap ~10 min).
            for (let i = 0; i < 400; i++) {
                await new Promise(r => setTimeout(r, 1500));
                const s = (await getInstallsBackfillStatus()).data || {};
                if (s.running) {
                    setBackfillMsg(`Geocoding ${s.done ?? 0}/${s.total ?? 0} · ${s.ok ?? 0} added${s.fail ? ` · ${s.fail} failed` : ''}…`);
                } else {
                    setBackfillMsg((s.total ?? 0) === 0
                        ? 'Map is already up to date — no new installs to add.'
                        : `Done — ${s.ok ?? 0} new install${(s.ok ?? 0) === 1 ? '' : 's'} added${s.fail ? ` (${s.fail} could not be located)` : ''}. Re-run a search to see them.`);
                    break;
                }
            }
        } catch (e) {
            setBackfillMsg('Update failed: ' + (e?.response?.data || e.message || 'error'));
        } finally {
            setBackfilling(false);
        }
    }, []);

    useEffect(() => { if (defaultQuery) setQuery(defaultQuery); }, [defaultQuery]);

    // Init the Leaflet map once.
    useEffect(() => {
        if (mapRef.current || !mapElRef.current) return;
        const map = L.map(mapElRef.current, { scrollWheelZoom: false }).setView([3.139, 101.6869], 11);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors', maxZoom: 19,
        }).addTo(map);
        layerRef.current = L.layerGroup().addTo(map);
        mapRef.current = map;
        setTimeout(() => map.invalidateSize(), 250);
        return () => { map.remove(); mapRef.current = null; layerRef.current = null; };
    }, []);

    const search = useCallback(async () => {
        const q = query.trim();
        if (!q) { setError('Enter an area, postcode or address.'); return; }
        setLoading(true); setError(''); setCount(null);
        try {
            const g = await geocodeAddress(q);
            const lat = Number(g.data.lat), lng = Number(g.data.lng);
            const map = mapRef.current, layer = layerRef.current;
            if (!map || !layer) return;
            layer.clearLayers();
            map.setView([lat, lng], radius <= 5 ? 13 : radius <= 10 ? 12 : 11);
            map.invalidateSize();

            // Prospect location + radius ring.
            L.circleMarker([lat, lng], { radius: 8, color: '#1A8CFF', fillColor: '#1A8CFF', fillOpacity: 0.9, weight: 2 })
                .addTo(layer).bindPopup('Prospect location');
            L.circle([lat, lng], { radius: radius * 1000, color: '#1A8CFF', weight: 1, fillOpacity: 0.05 }).addTo(layer);

            const res = await getNearbyInstalls(lat, lng, radius);
            const items = res.data.items || [];
            setCount(res.data.count ?? items.length);

            // Spread pins that share the same (area-level) coordinate so each stays clickable.
            const seen = {};
            items.forEach(it => {
                let plat = Number(it.lat), plng = Number(it.lng);
                const key = `${plat.toFixed(4)},${plng.toFixed(4)}`;
                const n = (seen[key] = (seen[key] || 0) + 1);
                if (n > 1) {
                    const ang = n * 2.39996, d = 0.0009 * Math.ceil(n / 6);
                    plat += d * Math.cos(ang); plng += d * Math.sin(ang);
                }
                const html =
                    `<div style="font-family:${PP};font-size:12px;min-width:170px">
                       <div style="font-weight:700;margin-bottom:2px">${esc(it.customerName)}</div>
                       <div>${esc(it.area)} &middot; ${it.distanceKm} km</div>
                       ${Number(it.kwp) > 0 ? `<div>${Number(it.kwp).toFixed(2)} kWp${it.phase === '3phase' ? ' &middot; 3-phase' : ''}</div>` : ''}
                       <div>Installed ${fmtDate(it.installDate)}</div>
                       <div style="color:#64748B">Closed by ${esc(it.agent)}</div>
                     </div>`;
                L.marker([plat, plng]).addTo(layer).bindPopup(html);
            });
        } catch (e) {
            const msg = e?.response?.data;
            setError(typeof msg === 'string' && msg ? msg : 'Search failed — try a broader area or just the postcode.');
        } finally { setLoading(false); }
    }, [query, radius]);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                <TextField
                    size="small" label="Area / postcode / address" value={query}
                    onChange={e => setQuery(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') search(); }}
                    sx={{ minWidth: 260, flex: 1 }}
                    placeholder="e.g. 41200 Klang Selangor"
                />
                <TextField size="small" select label="Radius" value={radius} onChange={e => setRadius(Number(e.target.value))} sx={{ width: 110 }}>
                    {[5, 10, 15, 20].map(r => <MenuItem key={r} value={r}>{r} km</MenuItem>)}
                </TextField>
                <Button variant="contained" onClick={search} disabled={loading}
                    sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', boxShadow: 'none' }}>
                    {loading ? <CircularProgress size={18} thickness={5} sx={{ color: '#fff' }} /> : 'Show neighbours'}
                </Button>
                {isSuperAdmin && (
                    <Button variant="outlined" onClick={updateMap} disabled={backfilling}
                        title="Geocode newly-added Active installs so they appear on the map"
                        sx={{ fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', borderColor: '#0F766E', color: '#0F766E', '&:hover': { borderColor: '#0B5A54', backgroundColor: 'rgba(15,118,110,0.08)' } }}>
                        {backfilling ? <CircularProgress size={18} thickness={5} sx={{ color: '#0F766E' }} /> : 'Update map (new installs)'}
                    </Button>
                )}
            </Box>
            {backfillMsg && <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#0F766E', fontFamily: PP, mb: 1 }}>{backfillMsg}</Typography>}

            {error && <Typography sx={{ fontSize: '0.78rem', color: '#DC2626', fontFamily: PP, mb: 1 }}>{error}</Typography>}
            {count != null && !error && (
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#0F62CC', fontFamily: PP, mb: 1 }}>
                    {count} neighbour{count === 1 ? '' : 's'} installed within {radius} km
                </Typography>
            )}

            <Box ref={mapElRef} sx={{ height: 380, width: '100%', borderRadius: '10px', overflow: 'hidden', border: '1px solid #E2E8F0' }} />
        </Box>
    );
}
